export const instruments = [
  {
    id: "piano",
    name: "Piano",
    category: "Keyboard Instruments",
    description:
      "Structured repertoire, technical work, sight-reading and aural skills across classical and contemporary styles.",
  },
  {
    id: "violin",
    name: "Violin",
    category: "String Instruments",
    description:
      "Progressive development of tone, intonation, bow control and musical interpretation through graded repertoire.",
  },
  {
    id: "guitar",
    name: "Classical & Acoustic Guitar",
    category: "Plucked String Instruments",
    description:
      "Fingerstyle and plectrum approaches, chord vocabulary, rhythm work and solo performance studies.",
  },
  {
    id: "voice",
    name: "Vocals (Western Contemporary / Indian Classical)",
    category: "Vocal Studies",
    description:
      "Structured syllabi for Western contemporary singing, Hindustani classical vocal study and Bollywood playback-style performance, with emphasis on breath control, pitch stability, stylistic nuance and text delivery.",
  },
  {
    id: "drums",
    name: "Drum Kit",
    category: "Percussion",
    description:
      "Time-keeping, coordination, stylistic grooves and solo construction across popular music genres.",
  },
  {
    id: "alto-saxophone",
    name: "Alto Saxophone",
    category: "Woodwind Instruments",
    description:
      "Embouchure development, breath control, articulation, scales and stylistic interpretation across classical, jazz and contemporary genres.",
  },
  {
    id: "ai-voice",
    name: "AI Voice Model Trainer & Voiceover Artist Master Certification",
    category: "Voice & AI Technology",
    description:
      "Comprehensive training in voiceover artistry, audio production, AI voice synthesis, model training, and professional voice industry skills. Specialization available in: English, Hindi, French, German, Spanish, or Japanese.",
  },
  {
    id: "music-therapy",
    name: "Professional Music Therapy Certification",
    category: "Therapy & Wellness",
    description:
      "Learn to use music as a therapeutic tool for emotional, cognitive, and behavioral wellbeing. Covers therapy techniques, session planning, ethics, and professional practice.",
  },
];

export const levels = [
  {
    id: 1,
    title: "Level 1",
    descriptor: "Foundation stage focusing on basic technique, secure posture and fundamental musical literacy.",
  },
  {
    id: 2,
    title: "Level 2",
    descriptor:
      "Lower intermediate stage consolidating core skills, simple repertoire and confidence in performance.",
  },
  {
    id: 3,
    title: "Level 3",
    descriptor:
      "Secure intermediate stage with broadened repertoire, improved control and developing stylistic awareness.",
  },
  {
    id: 4,
    title: "Level 4",
    descriptor:
      "Upper intermediate stage with increased technical demand and independent musical decision-making.",
  },
  {
    id: 5,
    title: "Level 5",
    descriptor:
      "Advanced stage demonstrating mature musical understanding, refined tone quality and reliable technique.",
  },
  {
    id: 6,
    title: "Level 6",
    descriptor:
      "Pre-professional stage evidencing artistic integrity, stylistic depth and readiness for higher specialist study.",
  },
];

export const syllabusGrades = {
  instrumental: {
    grade1: {
      title: "Grade 1",
      duration: "Recommended study duration: 6–12 months",
      format: [
        "Performance, technical work, sight reading and aural tests.",
        "Approximate examination time: 20 minutes.",
      ],
      marksTable: [
        { component: "Performance pieces (2)", marks: "40 marks" },
        { component: "Technical work", marks: "20 marks" },
        { component: "Sight reading", marks: "10 marks" },
        { component: "Aural tests", marks: "10 marks" },
      ],
      technicalRequirements: [
        "Major and minor scales in one octave, hands separately where applicable.",
        "Simple technical studies or exercises chosen from the recommended list.",
      ],
      prescribedPieces: [
        "Two contrasting pieces selected from the Grade 1 repertoire list for the relevant instrument.",
        "Pieces should demonstrate basic control of tone, rhythm and articulation.",
      ],
      sightReading: [
        "Short, unprepared exercises in simple time signatures and keys appropriate to the grade.",
      ],
      auralTests: [
        "Echo clapping of short rhythmic patterns.",
        "Identification of changes in pitch or rhythm in a simple melody.",
      ],
    },
    grade2: {
      title: "Grade 2",
      duration: "Recommended study duration: 6–12 months",
      format: [
        "Performance, technical work, sight reading and aural tests.",
        "Approximate examination time: 20–25 minutes.",
      ],
      marksTable: [
        { component: "Performance pieces (3)", marks: "60 marks" },
        { component: "Technical work", marks: "20 marks" },
        { component: "Sight reading", marks: "10 marks" },
        { component: "Aural tests", marks: "10 marks" },
      ],
      technicalRequirements: [
        "Extended range scales and broken chords as specified for Grade 2.",
        "Further development of finger patterns or coordination appropriate to the instrument.",
      ],
      prescribedPieces: [
        "Three contrasting pieces from the Grade 2 repertoire list, demonstrating style and character.",
      ],
      sightReading: [
        "Short melodies in additional keys and time signatures, with simple dynamic markings.",
      ],
      auralTests: [
        "Recognition of basic dynamic contrasts and articulation.",
        "Clapping or singing back short rhythmic or melodic patterns.",
      ],
    },
  },
};


export const examModes = [
  {
    id: "live-online",
    name: "Live Online Examination",
    description:
      "Real-time assessments conducted via secure video platform, allowing direct interaction with the examiner.",
  },
];

export const liveOnlineExamDetails = {
  title: "Live Online Examination",
  subtitle: "Real-time Assessment with Direct Examiner Interaction",
  overview: "Our Live Online Examination provides a comprehensive, real-time assessment experience that mirrors the traditional in-person examination while offering the convenience and accessibility of remote participation. Candidates perform live before a qualified examiner through our secure video conferencing platform.",
  features: [
    {
      title: "Real-Time Performance",
      description: "Perform your prepared pieces live, just as you would in a traditional examination setting. The examiner observes your performance in real-time, assessing technique, musicality, and presentation."
    },
    {
      title: "Direct Examiner Interaction",
      description: "Engage directly with your examiner who can provide immediate instructions, ask questions about your pieces, and conduct sight-reading or aural tests as required by your syllabus level."
    },
    {
      title: "Secure Video Platform",
      description: "All examinations are conducted through our dedicated, secure video conferencing system designed specifically for music assessments, ensuring optimal audio quality and reliable connectivity."
    },
    {
      title: "Flexible Scheduling",
      description: "Choose from available examination slots that suit your schedule. Sessions are available across multiple time zones to accommodate candidates worldwide."
    },
    {
      title: "Immediate Feedback",
      description: "Receive verbal feedback at the conclusion of your examination, with detailed written assessment and marks provided within the standard results period."
    },
    {
      title: "Technical Support",
      description: "Our technical team is available before and during your examination to ensure your setup meets requirements and to resolve any connectivity issues promptly."
    }
  ],
  requirements: [
    "Stable high-speed internet connection (minimum 10 Mbps upload/download)",
    "Computer or tablet with functioning webcam and microphone",
    "Quiet, well-lit room with adequate space for performance",
    "Instrument in proper playing condition and tuned",
    "All required music scores and materials ready",
    "Valid identification document for verification"
  ],
  process: [
    {
      step: 1,
      title: "Registration & Scheduling",
      description: "Register through the Student Portal, select your instrument and level, and choose your preferred examination date and time from available slots."
    },
    {
      step: 2,
      title: "Technical Check",
      description: "Complete a mandatory technical check 24-48 hours before your examination to verify your equipment, internet connection, and room setup meet our requirements."
    },
    {
      step: 3,
      title: "Examination Day",
      description: "Join the secure video session 10 minutes before your scheduled time. Present your identification, and proceed with your examination as directed by the examiner."
    },
    {
      step: 4,
      title: "Results & Certification",
      description: "Receive your detailed results and feedback within 2-3 weeks. Successful candidates receive their official certificate digitally, with printed certificates available upon request."
    }
  ]
};

export const assessmentPrinciples = [
  "Consistency of standards across all instruments, levels and assessment modes.",
  "Clarity of criteria so that candidates and teachers understand expectations at every level.",
  "Constructive feedback that supports long-term musical development.",
  "Integrity and independence in all assessment decisions.",
];

export const faqs = [
  {
    question: "Who can register for the Global Music Conservatory certifications?",
    answer:
      "Students of any age may register through a teacher or independently, provided they are prepared for the technical and musical requirements of the chosen level.",
  },
  {
    question: "Do candidates need to complete every level in sequence?",
    answer:
      "Yes, it is compulsory to complete each level in order to progress to the next level certification. For example, only after completing Level 1 can a candidate appear for Level 2, then Level 3, and so on. This structured progression ensures that students build a strong foundation and develop their skills systematically before advancing to more challenging material.",
  },
  {
    question: "Are online examinations equivalent to in-person assessments?",
    answer:
      "Yes. All modes of examination follow the same syllabus, marking criteria and quality assurance processes.",
  },
  {
    question: "How long does it take to receive results and certificates?",
    answer:
      "Indicative timelines are communicated for each session. Results are typically released within a few weeks of the examination, followed by digital and, where applicable, printed certificates.",
  },
  {
    question: "What are the benefits of getting this certification?",
    answer:
      "Global Music Conservatory certifications provide internationally recognised credentials that validate your musical skills and knowledge. Benefits include: enhanced credibility for teaching and performing opportunities, structured skill development through a comprehensive syllabus, detailed feedback from qualified examiners to guide your musical growth, and official certificates that can support applications to music institutions worldwide. Additionally, students who complete all 6 levels of certification have the exclusive opportunity to join our Examiner Panel to monitor and assess Level 1 & 2 certification exams. Levels 3 to 6 examinations are assessed by qualified musicians and artists with a minimum of 10 years of music teaching or performing experience.",
  },
  {
    question: "Can I reschedule my examination after booking?",
    answer:
      "Yes, exam rescheduling is allowed only ONCE per registration. You can reschedule your exam up to 72 hours before the scheduled exam time through your Student Portal dashboard. Simply click the 'Reschedule Exam' button, select a new date and time, and submit. After rescheduling once, no further changes to the exam date will be permitted. Please plan carefully before booking your exam.",
  },
  {
    question: "Are examination fees refundable?",
    answer:
      "No, examination fees are strictly non-refundable and non-transferable. Once payment is made, the fees cannot be refunded under any circumstances, nor can they be transferred to another candidate or a different examination. Please ensure you are fully prepared and have selected the correct exam date before completing your payment.",
  },
  {
    question: "Can I cancel my examination after booking?",
    answer:
      "No, once an examination is booked and the fee is paid, it cannot be cancelled. However, you may reschedule your exam ONCE, provided you do so at least 72 hours before the scheduled exam time. If you do not appear for your exam and have not rescheduled within the allowed timeframe, the examination fee will be forfeited. We strongly recommend confirming your availability before completing your registration.",
  },
];

export const sessionsAndFees = {
  sessions: [
    {
      name: "January–March Session",
      description: "Early-year session ideal for schools and institutions aligning with academic terms.",
    },
    {
      name: "June–August Session",
      description: "Mid-year session supporting summer programmes and intensive study periods.",
    },
    {
      name: "October–December Session",
      description: "End-of-year session suitable for candidates completing annual study plans.",
    },
  ],
  notes: [
    "Fees may vary by region and examination mode.",
    "Detailed fee schedules are provided within the secure teacher and student portals.",
  ],
};

export const examinerProfilePoints = [
  "Recognised qualifications in performance, pedagogy or music education.",
  "Extensive experience in teaching and assessing candidates across a range of levels.",
  "Commitment to impartiality, confidentiality and professional conduct.",
  "Ongoing participation in examiner training and standardisation activities.",
];
